/**
 * Due Diligence (startup-facing) — the founder's DD hub.
 *
 * Two segments:
 *   • Running   — active `shared_dd` sessions on this startup (who is diligencing
 *                 you + since when). Session-level only; the investor's private
 *                 notes/conclusions are never exposed (founderSessionFromRow).
 *   • Completed — concluded DDs: rich shared reports (get_shared_dd_reports()
 *                 SECURITY DEFINER — private amount/notes withheld) plus any
 *                 closed session whose report was not shared.
 *
 * Reached from the dashboard "Due Diligence" card: the Running/Completed count
 * tiles deep-link here via ?filter=running|completed; the "Shared reports" row
 * opens the Completed segment.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import AnimatedPressable from '@/components/AnimatedPressable';
import Animated, { FadeIn, FadeInUp, FadeInDown } from 'react-native-reanimated';
import { Briefcase, ThumbsUp, ThumbsDown, Clock, BarChart3, FileText, CheckCircle2, ChevronLeft, MessageCircle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { TierGate, UpgradePrompt } from '@/components/subscription';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSharedDDReportsForMyStartup } from '@/lib/dd/use-dd-reports';
import { useSharedDDsForMyStartup } from '@/lib/shared-dd';
import type { SharedDueDiligence } from '@/lib/shared-dd/types';
import { scoreToLabel, labelColor, capitalizeLabel } from '@/lib/scoring/score-display';
import type { SharedReportView } from '@/lib/dd/shared-report';
import { APP_LOCALE } from '@/lib/format';

const BREADCRUMBS = [
  { label: 'Dashboard', href: '/(startup)/startup-dashboard' },
  { label: 'Due Diligence' },
];

type Segment = 'running' | 'completed';

/** Lead (non-owner) investor name for a founder-visible session. */
function leadInvestorName(dd: SharedDueDiligence): string {
  return (
    dd.collaborators.find((c) => c.role !== 'owner')?.investorName ??
    dd.collaborators[0]?.investorName ??
    'Investor'
  );
}

function decisionConfig(decision: string, colors: ReturnType<typeof useThemeColors>) {
  switch (decision) {
    case 'invest':
    case 'committed':
      return { label: decision === 'committed' ? 'Committed' : 'Invest', color: colors.success, bg: colors.traffic.greenLight, Icon: ThumbsUp };
    case 'pass':
      return { label: 'Pass', color: colors.error, bg: colors.red.light, Icon: ThumbsDown };
    default:
      return { label: capitalizeLabel(decision), color: colors.warning, bg: colors.amber.light, Icon: Clock };
  }
}

/** Compact session row. Active sessions are tappable → the investor conversation. */
function SessionRow({ dd, onPress }: { dd: SharedDueDiligence; onPress?: () => void }) {
  const colors = useThemeColors();
  const active = dd.status === 'active';
  const when = active ? dd.createdAt : dd.updatedAt;
  const whenLabel = when ? new Date(when).toLocaleDateString(APP_LOCALE) : null;
  const rowStyle = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 12,
    backgroundColor: colors.surface.primary,
    borderWidth: 1,
    borderColor: colors.border.light,
    borderRadius: 14,
    padding: 14,
  };
  const body = (
    <>
      <View style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: active ? colors.primary[50] : colors.surface.secondary, alignItems: 'center', justifyContent: 'center' }}>
        {active ? <Briefcase size={16} color={colors.primary[600]} /> : <CheckCircle2 size={16} color={colors.text.tertiary} />}
      </View>
      <View style={{ flex: 1 }}>
        <Text style={{ color: colors.text.primary, fontSize: 13, fontWeight: '700' }} numberOfLines={1}>
          {leadInvestorName(dd)}
        </Text>
        <Text style={{ color: colors.text.tertiary, fontSize: 11, marginTop: 2 }} numberOfLines={1}>
          {active
            ? whenLabel ? `In progress since ${whenLabel}` : 'In progress'
            : whenLabel ? `Concluded ${whenLabel} · report not shared` : 'Concluded · report not shared'}
        </Text>
      </View>
      {active && (
        <View style={{ backgroundColor: colors.traffic.greenLight, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999 }}>
          <Text style={{ color: colors.success, fontSize: 10, fontWeight: '700', letterSpacing: 0.3, textTransform: 'uppercase' }}>
            Active
          </Text>
        </View>
      )}
      {onPress && <MessageCircle size={16} color={colors.primary[600]} />}
    </>
  );
  if (onPress) {
    return <AnimatedPressable onPress={onPress} style={rowStyle}>{body}</AnimatedPressable>;
  }
  return <View style={rowStyle}>{body}</View>;
}

function ReportCard({ report }: { report: SharedReportView }) {
  const colors = useThemeColors();
  const [expanded, setExpanded] = useState(false);
  const dc = decisionConfig(report.decision, colors);
  const notablePoints = report.notablePoints ?? [];
  const pointColor = (type: string) =>
    type === 'strength' ? colors.success : type === 'concern' ? colors.error : colors.text.secondary;
  const start = report.scoresAtStart;
  const end = report.scoresAtEnd;

  return (
    <AnimatedPressable
      onPress={() => setExpanded((e) => !e)}
      className="rounded-2xl overflow-hidden"
      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, padding: 16 }}
    >
      {/* Header */}
      <View className="flex-row items-start justify-between mb-2">
        <View className="flex-row items-center gap-3 flex-1">
          <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: `${colors.primary[600]}15` }}>
            <Briefcase size={16} color={colors.primary[600]} />
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.primary }} className="font-semibold" numberOfLines={1}>
              {report.investorName}
            </Text>
            {!!report.investorFirm && (
              <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{report.investorFirm}</Text>
            )}
          </View>
        </View>
        <View className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5" style={{ backgroundColor: dc.bg }}>
          <dc.Icon size={13} color={dc.color} />
          <Text style={{ color: dc.color }} className="text-xs font-semibold">{dc.label}</Text>
        </View>
      </View>

      {!!report.executiveSummary && (
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed" numberOfLines={expanded ? undefined : 2}>
          {report.executiveSummary}
        </Text>
      )}

      {expanded && (
        <Animated.View entering={FadeIn.duration(200)} className="mt-4 pt-4 border-t" style={{ borderColor: colors.border.light }}>
          {/* Score before/after */}
          {start && end && (
            <View className="rounded-xl p-3 mb-4" style={{ backgroundColor: colors.surface.secondary }}>
              <Text style={{ color: colors.text.primary }} className="font-semibold mb-2 text-sm">Score change during DD</Text>
              <View className="flex-row items-center justify-between">
                <Text style={{ color: colors.text.secondary }} className="text-xs">
                  {start.overall} ({capitalizeLabel(scoreToLabel(start.overall))})
                </Text>
                <Text style={{ color: end.overall >= start.overall ? colors.success : colors.error }} className="text-xs font-semibold">
                  → {end.overall} ({capitalizeLabel(scoreToLabel(end.overall))})
                </Text>
                <Text style={{ color: labelColor(scoreToLabel(end.overall)) }} className="text-[10px] font-semibold">
                  {end.overall - start.overall >= 0 ? '+' : ''}{end.overall - start.overall}
                </Text>
              </View>
            </View>
          )}

          {!!report.investmentThesis && (
            <>
              <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">Investment Thesis</Text>
              <Text style={{ color: colors.text.secondary }} className="text-sm mb-4 leading-relaxed">{report.investmentThesis}</Text>
            </>
          )}
          {!!report.keyRisks && (
            <>
              <Text style={{ color: colors.text.primary }} className="font-semibold mb-2">Key Risks</Text>
              <View className="rounded-xl p-3 mb-4" style={{ backgroundColor: colors.red.light }}>
                <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">{report.keyRisks}</Text>
              </View>
            </>
          )}
          {notablePoints.length > 0 && (
            <>
              <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">Notable Points</Text>
              <View className="gap-2 mb-4">
                {notablePoints.map((p, i) => (
                  <View key={`np-${i}`} className="flex-row items-start gap-2 p-2 rounded-lg" style={{ backgroundColor: `${pointColor(p.type)}10` }}>
                    <View className="w-2 h-2 rounded-full mt-1.5" style={{ backgroundColor: pointColor(p.type) }} />
                    <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">{p.text}</Text>
                  </View>
                ))}
              </View>
            </>
          )}
          {!!report.metrics?.length && (
            <>
              <Text style={{ color: colors.text.primary }} className="font-semibold mb-3">Metrics (at time of review)</Text>
              <View className="flex-row flex-wrap gap-2 mb-2">
                {report.metrics.map((m) => (
                  <View key={m.key} className="flex-row items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                    <BarChart3 size={14} color={m.source === 'deck' ? colors.primary[600] : colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{m.label}</Text>
                    <Text style={{ color: colors.text.secondary }} className="text-sm font-medium" numberOfLines={1}>{m.value}</Text>
                  </View>
                ))}
              </View>
            </>
          )}
          <View className="flex-row items-center gap-1.5 mt-1">
            <FileText size={13} color={colors.text.tertiary} />
            <Text style={{ color: colors.text.tertiary }} className="text-xs">{report.documentsReviewed} documents reviewed</Text>
          </View>
        </Animated.View>
      )}

      <View className="flex-row justify-center mt-3">
        <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">{expanded ? 'Show less' : 'View full report'}</Text>
      </View>
    </AnimatedPressable>
  );
}

function SegmentTab({ label, count, selected, onPress }: { label: string; count: number; selected: boolean; onPress: () => void }) {
  const colors = useThemeColors();
  return (
    <View style={{ flex: 1, borderRadius: 10, overflow: 'hidden', backgroundColor: selected ? colors.surface.primary : 'transparent' }}>
      <AnimatedPressable onPress={onPress} style={{ paddingVertical: 8, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: selected ? colors.text.primary : colors.text.tertiary, fontSize: 13, fontWeight: '700' }} numberOfLines={1}>
          {label} ({count})
        </Text>
      </AnimatedPressable>
    </View>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  const colors = useThemeColors();
  return (
    <Animated.View entering={FadeInUp.duration(300)} className="rounded-2xl p-6 items-center" style={{ backgroundColor: colors.surface.secondary }}>
      <Text style={{ color: colors.text.secondary }} className="font-semibold mb-1">{title}</Text>
      <Text style={{ color: colors.text.tertiary }} className="text-sm text-center">{body}</Text>
    </Animated.View>
  );
}

export default function DueDiligenceScreen() {
  useTrackScreen('DueDiligence');
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const router = useRouter();
  const { filter } = useLocalSearchParams<{ filter?: string }>();
  const startupId = useProfileStore((s) => s.startupProfile?.id);
  const { data: sessions = [] } = useSharedDDsForMyStartup(startupId);
  const { data: reports = [], isLoading } = useSharedDDReportsForMyStartup(startupId);

  const running = useMemo(() => sessions.filter((s) => s.status === 'active'), [sessions]);
  const closed = useMemo(() => sessions.filter((s) => s.status === 'closed'), [sessions]);

  // Closed sessions whose report was NOT shared (dedupe against reports by investor name).
  const closedWithoutReport = useMemo(() => {
    const reported = new Set(reports.map((r) => r.investorName?.trim().toLowerCase()).filter(Boolean));
    return closed.filter((s) => !reported.has(leadInvestorName(s).trim().toLowerCase()));
  }, [closed, reports]);

  const [segment, setSegment] = useState<Segment>(filter === 'completed' ? 'completed' : 'running');
  useEffect(() => {
    if (filter === 'running' || filter === 'completed') setSegment(filter);
  }, [filter]);

  return (
    <AppLayout title="Due Diligence" breadcrumbs={BREADCRUMBS}>
      <SafeAreaView edges={isWeb ? [] : ['top']} style={{ flex: 1 }}>
        {/* Mobile header — web gets breadcrumbs from AppLayout, mobile has none, so add a back + title */}
        {phoneShell && (
          <Animated.View
            entering={FadeInDown.duration(300)}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingTop: 8, paddingBottom: 8 }}
          >
            <Pressable
              onPress={() => router.back()}
              style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: colors.surface.secondary, alignItems: 'center', justifyContent: 'center' }}
            >
              <ChevronLeft size={22} color={colors.text.primary} />
            </Pressable>
            <Text style={{ color: colors.text.primary, fontSize: 20, fontWeight: '800' }} numberOfLines={1}>
              Due Diligence
            </Text>
          </Animated.View>
        )}

        <TierGate
          feature="direct_messaging"
          fallback={
            <View style={{ padding: 16 }}>
              <UpgradePrompt targetTier="standard" variant="card" headline="Due Diligence" lockedTitle="Due diligence" maxFeatures={3} />
            </View>
          }
        >
          <ScrollView contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 48 }} showsVerticalScrollIndicator={false}>
            {/* Segment toggle */}
            <View className="flex-row" style={{ backgroundColor: colors.surface.secondary, borderRadius: 12, padding: 4, gap: 4 }}>
              <SegmentTab label="Running" count={running.length} selected={segment === 'running'} onPress={() => setSegment('running')} />
              <SegmentTab label="Completed" count={closed.length} selected={segment === 'completed'} onPress={() => setSegment('completed')} />
            </View>

            {segment === 'running' ? (
              <>
                <Text style={{ color: colors.text.tertiary }} className="text-sm mb-1">
                  Investors currently running due diligence on your startup. Tap to open the conversation.
                </Text>
                {running.length === 0 ? (
                  <EmptyState title="No active due diligence" body="When an investor starts due diligence on your startup, it will appear here." />
                ) : (
                  running.map((dd) => (
                    <SessionRow key={dd.id} dd={dd} onPress={() => router.push('/(startup)/startup-messages')} />
                  ))
                )}
              </>
            ) : (
              <>
                <Text style={{ color: colors.text.tertiary }} className="text-sm mb-1">
                  Concluded due diligence. Shared assessments show the investor's report; their private notes are never included.
                </Text>
                {!isLoading && reports.length === 0 && closedWithoutReport.length === 0 ? (
                  <EmptyState title="No completed due diligence yet" body="When an investor concludes due diligence — and shares their report — it will appear here." />
                ) : (
                  <>
                    {reports.map((r) => <ReportCard key={r.id} report={r} />)}
                    {closedWithoutReport.map((dd) => <SessionRow key={dd.id} dd={dd} />)}
                  </>
                )}
              </>
            )}
          </ScrollView>
        </TierGate>
      </SafeAreaView>
    </AppLayout>
  );
}
